// eslint-disable-next-line import/no-unassigned-import
import './Header.css'

import { Assets, Components, Interfaces, Models, Sendum, Services } from '@sendum/core'
import classNames from 'classnames'
import React, { useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import SelectSearch, * as SelectSearchUtils from 'react-select-search'

const VISIBILITY_PUBLIC_REGEXP = /^\/visibility\/public$/g;

export const Header = (): JSX.Element => {
    const timer = useRef<ReturnType<typeof setInterval>>()
    const TIMER_INTERVAL = 1000
    const [isInitiatedApiClient, setInitiatedApiClient] = useState<boolean>(false)

    const [shipments, setShipments] = useState<Models.Shipment[]>([])
    const [masterTrackingNo, setMasterTrackingNo] = useState<string>()
    const [isAuthorizationPage, setAuthorizationPage] = useState<boolean>(
        /^\/(login|forgot-password|reset-password)/g.test(window.location.pathname)
    )
    const [showUserInfo, setShowUserInfo] = useState<boolean>(
        !/^\/visibility\/(public|shared|notification)/g.test(window.location.pathname)
    )
    const [hideShipmentSelector, setHideShipmentSelector] = useState<boolean>(
        /^\/visibility\/(public|notification|virtual)|\/shipments/g.test(window.location.pathname)
    )
    const [hideDownloadData, setHideDownloadData] = useState<boolean>(
        /^\/shipments$|\/visibility$/g.test(window.location.pathname)
    )

    const [showClearCache, setShowClearCache] = useState<boolean>(
        VISIBILITY_PUBLIC_REGEXP.test(window.location.pathname)
    )

    const locationChangeHandler = (): void => {
        setAuthorizationPage(/^\/(login|forgot-password|reset-password)/g.test(window.location.pathname))
        setShowUserInfo(!/^\/visibility\/(public|shared|notification)/g.test(window.location.pathname))
        setHideShipmentSelector(
            /^\/visibility\/(public|virtual|notification)|\/shipments/g.test(window.location.pathname)
        )
        setHideDownloadData(/^\/shipments$|\/visibility$/g.test(window.location.pathname))
        setShowClearCache(VISIBILITY_PUBLIC_REGEXP.test(window.location.pathname))
    }

    const timerHandler = async (): Promise<void> => {
        try {
            await Sendum.getWidgetsApiClient()
            setInitiatedApiClient(true)
            if (timer.current) {
                clearInterval(timer.current)
                timer.current = undefined
            }
        } catch (ignore) {
            setInitiatedApiClient(false)
        }
    }

    React.useEffect(() => {
        Sendum.getBrowserHistory().listen(locationChangeHandler)
        timer.current = setInterval(() => void timerHandler(), TIMER_INTERVAL)

        return () => {
            if (timer.current) {
                clearInterval(timer.current)
            }
        }
    }, [])

    React.useEffect(() => {
        if (!hideShipmentSelector && isInitiatedApiClient) {
            Services.API.WidgetsApi.getShipments({ view: 'groups', limit: 1000 })
                .then((_shipments: Models.Shipment[]) => {
                    setShipments(_shipments)
                    const path: string[] = Sendum.getBrowserHistory().location.pathname.split('/')
                    if (['visibility', 'timeline'].includes(path[1]) && !!path[2]) {
                        setMasterTrackingNo(decodeURIComponent(path[2]))
                    }
                    if (['shared'].includes(path[2]) && !!path[3]) {
                        setMasterTrackingNo(decodeURIComponent(path[3]))
                    }
                })
                .catch((e: unknown) => {
                    const error = e as Services.API.ApiError
                    if (error.message) {
                        toast.error(error.message)

                        return
                    }

                    if (error.type === 'ServerError') {
                        const sError = error as Services.API.ServerError
                        toast.error((sError.data as Interfaces.IAnyObject)?.error as string)
                    }

                    toast.error(error.type)
                })
        }
    }, [hideShipmentSelector, isInitiatedApiClient])

    const onClickAuthBtnHandler = (): void => {
        const user = new Models.User()
        if (user.isAuthorized()) {
            user.logout()
        } else {
            Sendum.getBrowserHistory().push('/login')
        }
        user.refresh()
    }

    const getAuthBtnTitle = (): string => (new Models.User().isAuthorized() ? 'Logout' : 'Login')

    const selectSearchOnChangeHandler = (
        selectedValue: SelectSearchUtils.SelectedOptionValue | SelectSearchUtils.SelectedOptionValue[]
    ): void => {
        if (!Array.isArray(selectedValue)) {
            setMasterTrackingNo(`${(selectedValue as unknown) as string}`)

            if (Sendum.getBrowserHistory().location.pathname === '/') {
                Sendum.getBrowserHistory().push(`/visibility/${(selectedValue as unknown) as string}`)

                return
            }

            const path: string[] = Sendum.getBrowserHistory().location.pathname.split('/')
            if (path.length > 2) {
                path.splice(-1, 1)
            }
            Sendum.getBrowserHistory().push(`${path.join('/')}/${(selectedValue as unknown) as string}`)
        }
    }

    const shipmentsList: JSX.Element | undefined = hideShipmentSelector ? (
        undefined
    ) : (
        <SelectSearch
            options={shipments.map(_ => ({ name: _.masterTrackingNo, value: _.masterTrackingNo }))}
            value={masterTrackingNo}
            search={true}
            filterOptions={SelectSearchUtils.fuzzySearch}
            placeholder="Select shipment"
            onChange={selectSearchOnChangeHandler}
        />
    )

    const onClickLogoHandler = (): void => {
        Sendum.getBrowserHistory().push('/')
    }

    return (
        <div className={'header'}>
            <Components.CustomToaster />
            {!isAuthorizationPage && (
                <div className={'header__row'}>
                    <div className={classNames('header__row_item', 'header__whitelabel')}>
                        <img
                            src={Assets.Customers.Marken_UPS_Logo}
                            alt={''}
                            onClick={onClickLogoHandler}
                            data-testid="logo"
                        />
                    </div>
                    <div className={classNames('header__shipmentSelector', 'header__row_item')}>{shipmentsList}</div>
                    {showUserInfo && (
                        <div className={classNames('header__username', 'header__row_item')}>
                            {new Models.User().getUsername()}
                        </div>
                    )}
                    {showUserInfo && (
                        <div className={'header__row_item'}>
                            <Components.Button title={getAuthBtnTitle()} onClick={onClickAuthBtnHandler} />
                        </div>
                    )}
                    <div className={'header__actionsContainer'}>
                        {!hideDownloadData && (
                            <div className={'header__row_item'}>
                                <Components.DownloadSelector awailebleCFR21 awailebleCFR21ClientView={showUserInfo} />
                            </div>
                        )}
                        {showClearCache && (
                            <div className={'header__row_item'}>
                                <Components.ClearCache/>
                            </div>
                        )}
                        <div className={'header__row_item'}>
                            <Components.TimeZoneSelector />
                        </div>
                    </div>
                </div>
            )}
            {isAuthorizationPage && (
                <div className={'header__row'}>
                    <div className={classNames('header__row_item', 'header__timeZoneSelector')}>
                        <Components.TimeZoneSelector />
                    </div>
                </div>
            )}
        </div>
    )
}
